/**
 * How a day-scoped cafeteria view renders while its day query is in flight.
 *
 * The service/delivery date lives INSIDE every cafeteria query key, so stepping
 * a day mints a brand-new cache entry. Read naively (`isPending` alone) that
 * blanks the whole page — including the header that owns the day chevrons, so
 * a POS operator loses the control they just tapped. Paired with
 * `placeholderData: keepPreviousData` on the hook, this splits the two cases
 * that `isPending` conflates:
 *
 * - COLD (no data at all) → skeleton, the only state that may blank a page.
 * - STALE (previous day still on screen) → render it dimmed and inert.
 *
 * Read-only surfaces take both. Write surfaces (the Confirm-deliveries and
 * Complete-service wizards, the Serve roster) must NOT render the stale case:
 * their drafts and mutations are keyed by the NEW day/mealtime, so a tap on a
 * stale row would commit against the wrong one. They pass
 * `allowStale: false` and get a skeleton for both.
 */

export interface DayQueryState {
  isPending: boolean
  isPlaceholderData: boolean
  hasData: boolean
}

export interface DayViewState {
  /** Blank the content region and show the skeleton. */
  showSkeleton: boolean
  /** Content is the PREVIOUS day — render it dimmed and non-interactive. */
  stale: boolean
}

export function cafeteriaDayViewState(query: DayQueryState, options: { allowStale?: boolean } = {}): DayViewState {
  const allowStale = options.allowStale ?? true
  const stale = query.isPlaceholderData && query.hasData
  if (!allowStale) {
    return { showSkeleton: query.isPending || stale, stale: false }
  }
  return { showSkeleton: query.isPending && !query.hasData, stale }
}
